import curry from 'lodash/curry'
import EXIF from 'exif-js'
import { Cloudinary } from '@cloudinary/base'
import { Resize } from '@cloudinary/base/actions/resize'
import config from 'config'
import { IMAGE_PROVIDERS } from 'constants/media'

const cloudinary = new Cloudinary({
  cloud: {
    cloudName: config.cloudinary.cloudName,
  },
  url: {
    secure: true,
  },
})

/**
 * Returns resized image as a JPEG data url.
 * @param {number} width - Width to resize the image to.
 * @param {number} height - Height to resize the image to.
 * @param {string} src - Image to resize
 */
const resize = (width, height, src) => {
  return new Promise((resolve, reject) => {
    const imgElement = new Image()
    imgElement.crossOrigin = 'Anonymous'
    imgElement.onload = function () {
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      canvas.getContext('2d').drawImage(imgElement, 0, 0, width, height)
      resolve(toJPEG(canvas))
    }
    imgElement.onerror = reject
    imgElement.src = src
  })
}

/**
 * Gets the canvas element as JPEG data url
 * @param {HTMLCanvasElement} canvas - The canvas element
 */
const toJPEG = canvas => canvas.toDataURL('image/jpeg', 8)

/**
 * Gets the element top position in relation to the document.
 * @param {Object} event - HTML event
 */
const getTop = event => (event.clientY || event.pageY || event.touches[0].clientY) + window.scrollY

/**
 * Gets the element left position in relation to the document.
 * @param {Object} event - HTML event
 */
const getLeft = event => (event.clientX || event.pageX || event.touches[0].clientX) + window.scrollX

/**
 * Scale a value from the actual to the intended scale
 * @param {number} value - Value to scale from 'from' to 'to'
 * @param {number[]} from - Current value scale
 * @param {number[]} to - Intended value scale
 */

const scale = curry((from, to, value) => {
  var scale = (to[1] - to[0]) / (from[1] - from[0])
  var capped = Math.min(from[1], Math.max(from[0], value)) - from[0]
  return ~~(capped * scale + to[0])
})

/**
 * Returns a string with the number and px at the end. Eg.: 10px
 * @param {number} number - Number to transform to px format.
 * @returns {string} Number with px at the end.
 */
const getInPx = number => `${number}px`

/**
 * Changes img src to a default url when retrieving the image causes an error
 * @param {Object} event - HTML event
 * @param {string} defaultUrl - Default image url to fallback to
 */
const defaultImageOnError = (event, defaultUrl) => {
  event.target.src = defaultUrl
}

const fixImageRotation = image => {
  return new Promise(resolve => {
    EXIF.getData(image, function () {
      const url = URL.createObjectURL(image)
      const img = new Image()
      img.onload = function () {
        URL.revokeObjectURL(url)
        const orientation = EXIF.getTag(this, 'Orientation')
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        canvas.width = this.width
        canvas.height = this.height
        const styleWidth = canvas.style.width
        const styleHeight = canvas.style.height
        if (orientation) {
          if (orientation > 4) {
            canvas.width = this.height
            canvas.style.width = styleHeight
            canvas.height = this.width
            canvas.style.height = styleWidth
          }
          // eslint-disable-next-line default-case
          switch (orientation) {
            case 2:
              ctx.translate(this.width, 0)
              ctx.scale(-1, 1)
              break
            case 3:
              ctx.translate(this.width, this.height)
              ctx.rotate(Math.PI)
              break
            case 4:
              ctx.translate(0, this.height)
              ctx.scale(1, -1)
              break
            case 5:
              ctx.rotate(0.5 * Math.PI)
              ctx.scale(1, -1)
              break
            case 6:
              ctx.rotate(0.5 * Math.PI)
              ctx.translate(0, -this.height)
              break
            case 7:
              ctx.rotate(0.5 * Math.PI)
              ctx.translate(this.width, -this.height)
              ctx.scale(-1, 1)
              break
            case 8:
              ctx.rotate(-0.5 * Math.PI)
              ctx.translate(-this.width, 0)
              break
          }
        }
        ctx.drawImage(img, 0, 0, this.width, this.height)
        resolve(canvas.toDataURL('image/jpg'))
      }
      img.src = url
    })
  })
}

const getImageUrl = ({ media, defaultUrl, width }) => {
  if (media.provider !== IMAGE_PROVIDERS.CLOUDINARY) {
    return defaultUrl
  }

  const image = cloudinary.image(media.providerData)
  image.resize(Resize.limitFit(width))

  return image.toURL()
}

export {
  resize,
  getTop,
  getLeft,
  scale,
  getInPx,
  defaultImageOnError,
  fixImageRotation,
  getImageUrl,
}
